/**
 * `shadow-modal`, `radius-xl`, `bg-surface`, max-width 440px, scrim
 * `--ud-color-scrim` (components.md §3).
 *
 * **Trading never opens a modal.** The six permitted uses are connect wallet,
 * deposit, withdraw, confirm-cancel-all, settings and choose a username
 * (§F136) — an order must be placeable without leaving the market, because the
 * market is sixty seconds long.
 * That is a rule about callers, not about this file, but it is the reason this
 * component is small and there is no `<ConfirmOrderModal>` anywhere.
 *
 * Focus handling is hand-rolled rather than pulled from a dialog library: the
 * native `<dialog>` element's `showModal()` moves focus and scroll-locks in ways
 * that fight a page which never scrolls, and a library would be a runtime
 * dependency on the money-moving path for about forty lines of behaviour.
 * Escape closes, focus is trapped, and focus returns to whatever opened it.
 */

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  /** Off for a modal the user must answer — confirm-cancel-all. */
  dismissible?: boolean
  className?: string
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissible = true,
  className,
}: ModalProps) {
  const panel = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)
  // **Read through a ref, so the effect below runs on open and close only.**
  // Callers pass `onClose` inline, so it is a new function on every render;
  // as a dependency it re-ran the effect on each keystroke, whose cleanup
  // restored focus behind the modal and whose body then focused the close
  // button — so a field inside a modal took one character and lost focus.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return

    restoreTo.current = document.activeElement as HTMLElement | null
    const node = panel.current
    const firstFocusable = node?.querySelector<HTMLElement>(FOCUSABLE)
    // Focus the panel itself when it has no controls, so Escape and the trap
    // still have somewhere to fire from.
    if (firstFocusable) firstFocusable.focus()
    else node?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !node) return

      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      restoreTo.current?.focus?.()
    }
  }, [open, dismissible])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--ud-color-scrim)' }}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          'flex w-full max-w-[calc(440*var(--ud-u))] flex-col gap-3 rounded-xl bg-bg-surface p-4 shadow-modal outline-none',
          'motion-safe:animate-[ud-modal-in_280ms_var(--ud-ease-out)_both]',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-title text-text-primary">{title}</h2>
          {dismissible ? (
            <button
              type="button"
              aria-label="close"
              onClick={onClose}
              className="inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] shrink-0 md:min-h-0 md:min-w-0 items-center justify-center rounded-sm px-1 text-body-lg text-text-tertiary hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="text-body-lg text-text-secondary">{children}</div>
        {footer ? <div className="flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  )
}
